import { useState } from "react";
import { Clock, Share2, MapPin, ExternalLink, ChevronRight, Check } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { Link } from "wouter";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { subscribeToNewsletter } from "@/lib/brevo";

// Article 3 hero: modern residential building Brasov mountains (Varianta A)
const HERO_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/article3-residential-complex-Vstb4C43kK3pRH4WYGacpF.webp";
// Article 3 in-article: luxury apartment interior with mountain view (Varianta B)
const STREET_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/hero-article3-v2-fQw8k2ttHhKYhRrnDtHWgL.webp"; // NOTE: this URL returns 200 - keeping original
const REALESTATE_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/hero-real-estate-oVkqv2kDQCBzogKNZUAGJw.webp";

function SidebarAd() {
  return (
    <div className="border border-stone-200 overflow-hidden">
      <div className="px-3 py-1.5" style={{ backgroundColor: "oklch(0.95 0.008 75)" }}>
        <span className="text-xs text-stone-400 uppercase tracking-widest">Partner Content · Cartier Transilvania</span>
      </div>
      <div className="relative">
        <img src="https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/XbbLvxSueOHmjHcF.webp" alt="Cartier Transilvania Brașov" className="w-full h-48 object-cover" loading="lazy" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, oklch(0.12 0.01 60 / 0.8) 0%, transparent 60%)" }} />
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <p className="text-white text-xs uppercase tracking-widest mb-1 font-semibold">New development · Noua</p>
          <h4 className="text-white font-bold leading-tight" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.1rem" }}>
            Cartier Transilvania
          </h4>
        </div>
      </div>
      <div className="p-4">
        <p className="text-stone-600 text-sm font-light leading-relaxed mb-3">
          208 apartments in 6 blocks. T1–T3 completed &amp; occupied. T4–T6 delivering September 2027. Next to Lake Noua.
        </p>
        <div className="flex items-center gap-1 text-xs text-stone-500 mb-4">
          <MapPin size={11} />
          <span>Noua, Brașov</span>
        </div>
        <a
          href="https://www.cartiertransilvania.ro/en/"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-1 text-xs text-white w-full px-3 py-2 transition-opacity hover:opacity-90"
          style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}>
          VIEW PROJECT <ExternalLink size={10} />
        </a>
      </div>
    </div>
  );
}

function InArticleBanner() {
  return (
    <div className="my-10 border border-stone-200 overflow-hidden">
      <div className="px-4 py-1.5" style={{ backgroundColor: "oklch(0.95 0.008 75)" }}>
        <span className="text-xs text-stone-400 uppercase tracking-widest">Partner content · Cartier Transilvania</span>
      </div>
      <div className="flex flex-col sm:flex-row">
        <img src="https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/XbbLvxSueOHmjHcF.webp" alt="Cartier Transilvania" className="w-full sm:w-48 h-36 object-cover flex-shrink-0" loading="lazy" />
        <div className="p-5 flex flex-col justify-between">
          <div>
            <p className="text-xs uppercase tracking-widest font-semibold mb-2" style={{ color: "oklch(0.38 0.12 15)" }}>
              Apartments for sale in Brașov
            </p>
            <h4 className="font-bold text-stone-900 mb-2 leading-snug" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.05rem" }}>
              Cartier Transilvania — 208 apartments in Noua
            </h4>
            <p className="text-stone-500 text-sm font-light leading-relaxed">
              T1, T2, T3 completed and occupied. T4, T5, T6 under construction — delivery September 2027. Solar panels &amp; heat pump heating.
            </p>
          </div>
          <div className="flex items-center gap-3 mt-4">
            <a
              href="https://www.cartiertransilvania.ro/en/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs text-white px-4 py-2 transition-opacity hover:opacity-90"
              style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}>
              VISIT PROJECT SITE <ChevronRight size={11} />
            </a>
            <span className="text-xs text-stone-400">cartiertransilvania.ro</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function FeaturedDeveloper() {
  return (
    <div className="mt-16 p-8 border-l-4" style={{ backgroundColor: "oklch(0.985 0.006 75)", borderLeftColor: "oklch(0.38 0.12 15)" }}>
      <div className="flex items-center gap-2 mb-5">
        <span className="text-xs text-stone-400 uppercase tracking-widest">Partner content · Cartier Transilvania</span>
      </div>
      <div className="flex flex-col md:flex-row gap-6 items-start">
        <img src="https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/XbbLvxSueOHmjHcF.webp" alt="Cartier Transilvania" className="w-full md:w-56 h-40 object-cover flex-shrink-0" loading="lazy" />
        <div>
          <p className="text-xs uppercase tracking-widest font-semibold mb-2" style={{ color: "oklch(0.38 0.12 15)" }}>
            Apartments for sale · Noua, Brașov
          </p>
          <h3 className="font-bold text-stone-900 mb-3 leading-snug" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.3rem" }}>
            Cartier Transilvania — a development built to last
          </h3>
          <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.7" }}>
            208 apartments across 6 blocks in the Noua neighbourhood. T1, T2, and T3 are completed and occupied. T4, T5, and T6 are under construction with a delivery date of September 2027. Solar panels and heat pump heating system included. Next to Lake Noua, Brașov Zoo, and Parc Aventura.
          </p>
          <a
            href="https://www.cartiertransilvania.ro/en/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-xs text-white px-5 py-2.5 transition-opacity hover:opacity-90"
            style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}>
            VISIT PROJECT SITE <ExternalLink size={11} />
          </a>
        </div>
      </div>
    </div>
  );
}

const ARTICLE1_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/hero-article1-v1-mBURY4muT8nNAjUnDwQRbz.webp";
const ARTICLE2_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/hero-article2-v1-ZjmAs5X6RgtMKCGR8QmQdR.webp";
const ARTICLE4_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/article4-market-hero_bed3bdbc.webp";
const ARTICLE5_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/article5-entertainment-hero-YwxKL9nvfxZqDmSm9YR8ym.webp";
const ARTICLE6_IMG = "https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/uLiraOubpeeGNSzh.webp";

const RELATED = [
  { tag: "Real Estate", title: "Buying an Apartment in Brașov in 2026: What to Look For, and One Development Worth Knowing", img: ARTICLE6_IMG, href: "/cartier-transilvania-brasov" },
  { tag: "Relocation", title: "Cost of Living in Brașov 2026: the honest numbers", img: ARTICLE4_IMG, href: "/cost-of-living-brasov" },
  { tag: "Lifestyle", title: "Entertainment in Brașov: Day & Night for Adults — with Real Prices", img: ARTICLE5_IMG, href: "/entertainment-brasov-guide" },
];

function NewsletterInlineLight() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle"|"loading"|"success"|"error">("idle");
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setStatus("loading");
    try {
      await subscribeToNewsletter(email);
      setStatus("success");
    } catch { setStatus("error"); }
  };
  if (status === "success") return <p className="text-xs text-green-700 py-2">✓ Subscribed! See you Friday.</p>;
  return (
    <form onSubmit={handleSubmit}>
      <input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" className="w-full border border-stone-200 px-3 py-2 text-xs text-stone-700 outline-none focus:border-stone-400 mb-2" />
      <button type="submit" disabled={status === "loading"} className="w-full py-2 text-xs text-white font-semibold disabled:opacity-60" style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}>
        {status === "loading" ? "..." : "SUBSCRIBE"}
      </button>
      {status === "error" && <p className="text-xs text-red-500 mt-1">Try again.</p>}
    </form>
  );
}

function ShareButton({ url, title }: { url: string; title: string }) {
  const [copied, setCopied] = useState(false);
  const handleShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title, url }); } catch { /* cancelled */ }
    } else {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  return (
    <button onClick={handleShare} className="flex items-center gap-2 text-xs text-stone-500 hover:text-stone-800 transition-colors">
      {copied ? <Check size={13} className="text-green-600" /> : <Share2 size={13} />}
      {copied ? "Copied!" : "Share"}
    </button>
  );
}

export default function ArticleRealEstate() {
  return (
    <>
    <Helmet>
      <title>Buying Property in Brașov: A Complete Guide for Foreigners (2026)</title>
      <meta name="description" content="How to buy property in Brașov as a foreigner. Prices by neighbourhood, legal steps, taxes, and what to watch out for as a non-resident. Updated 2026." />
      <meta name="keywords" content="buying property in Brasov, buy apartment Brasov, Brasov real estate prices, property market Brasov, Brasov apartments for sale" />
      <meta property="og:title" content="Buying Property in Brașov: A Complete Guide for Foreigners (2026)" />
      <meta property="og:description" content="How to buy property in Brașov as a foreigner. Prices by neighbourhood, legal steps, taxes, and what to watch out for as a non-resident. Updated 2026." />
      <meta property="og:image" content="https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/article3-residential-complex-Vstb4C43kK3pRH4WYGacpF.webp" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:type" content="article" />
      <meta property="og:url" content="https://brasovgateway.com/buying-apartment-brasov" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Buying Property in Brașov: A Complete Guide for Foreigners (2026)" />
      <link rel="canonical" href="https://brasovgateway.com/buying-apartment-brasov" />
      <script type="application/ld+json">{`
        {
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Article",
              "@id": "https://brasovgateway.com/buying-apartment-brasov#article",
              "headline": "Brașov real estate: a decade of prices that kept going up — and why they're not done yet",
              "description": "Ten years of Brașov property price data, neighbourhood analysis, and what foreign buyers need to know in 2026.",
              "image": "https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/XbbLvxSueOHmjHcF.webp",
              "datePublished": "2026-04-01",
              "dateModified": "2026-04-01",
              "author": {
                "@type": "Organization",
                "name": "Brașov Gateway",
                "url": "https://brasovgateway.com"
              },
              "publisher": {
                "@type": "Organization",
                "name": "Brașov Gateway",
                "url": "https://brasovgateway.com"
              },
              "url": "https://brasovgateway.com/buying-apartment-brasov",
              "inLanguage": "en",
              "articleSection": "Real Estate"
            },
            {
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Home",
                  "item": "https://brasovgateway.com"
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": "Blog",
                  "item": "https://brasovgateway.com/blog"
                },
                {
                  "@type": "ListItem",
                  "position": 3,
                  "name": "Brașov real estate: a decade of prices that kept going up — ...",
                  "item": "https://brasovgateway.com/buying-apartment-brasov"
                }
              ]
            }
          ]
        }
      `}</script>
    </Helmet>
    <div className="min-h-screen" style={{ fontFamily: "Source Sans 3, sans-serif", backgroundColor: "oklch(0.985 0.006 75)" }}>
      {/* NAV */}
      <SiteHeader />

      {/* HERO IMAGE */}
      <div className="relative h-72 md:h-96 mt-16">
        <img src={HERO_IMG} alt="Buying property in Brașov — modern residential development" className="w-full h-full object-cover" fetchPriority="high" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, transparent 40%, oklch(0.985 0.006 75) 100%)" }} />
      </div>

      <div className="container py-8">
        <div className="flex flex-col lg:flex-row gap-12">
          {/* MAIN CONTENT */}
          <main className="flex-1 min-w-0">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-xs uppercase tracking-widest font-semibold px-2 py-1" style={{ color: "oklch(0.38 0.12 15)", backgroundColor: "oklch(0.95 0.02 20)" }}>
                  Real estate
                </span>
                <div className="flex items-center gap-1 text-stone-400 text-xs">
                  <Clock size={11} />
                  <span>8 min read</span>
                </div>
              </div>
              <h1 className="text-stone-900 mb-4 leading-tight" style={{ fontFamily: "Playfair Display, serif", fontSize: "clamp(1.8rem, 4vw, 2.8rem)", fontWeight: 700 }}>
                Buying property in Brașov: a decade of prices that kept going up — and why they're not done yet
              </h1>
              <div className="flex items-center justify-between py-4 border-t border-b border-stone-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full overflow-hidden" style={{ backgroundColor: "oklch(0.95 0.008 75)" }}>
                    <img src="https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/XbbLvxSueOHmjHcF.webp" alt="Brașov Gateway" className="w-full h-full object-cover opacity-50" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-stone-900">Brașov Gateway Editorial</p>
                    <p className="text-xs text-stone-500">Legal &amp; Real Estate Team</p>
                  </div>
                </div>
                <ShareButton url="https://brasovgateway.com/buying-apartment-brasov" title="Buying Property in Brașov: A Complete Guide for Foreigners (2026)" />
              </div>
            </div>

            {/* ARTICLE BODY */}
            <div>
              <p className="text-stone-700 text-lg font-light leading-relaxed mb-6" style={{ lineHeight: "1.8" }}>
                Buying property in Brașov has become one of the most discussed topics among expats and foreign investors in Romania. There's a number that tends to silence a room when you mention it to someone who was looking at apartments in Brașov in 2015. Back then, you could buy a decent apartment in the city for around €800 per square metre. Today, that same square metre costs €2,272 — and that's the average. In some neighbourhoods and new developments, you're looking at €2,700 or more — <Link href="/cartier-transilvania-brasov" className="underline decoration-dotted underline-offset-2 hover:text-stone-900 transition-colors" style={{ color: "oklch(0.38 0.12 15)" }}>Cartier Transilvania in Noua</Link> is one example of a new development where prices sit in that range.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-8" style={{ lineHeight: "1.8" }}>
                That's not a typo. In roughly ten years, prices in Brașov have nearly tripled. The question worth asking — especially if you're considering buying now — is whether that curve is still going up, or whether you've already missed the train.
              </p>

              <h2 className="text-stone-900 mt-10 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                How we got here: the decade in numbers
              </h2>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                The story of Brașov's real estate market over the past decade breaks naturally into three distinct chapters.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                <strong className="text-stone-800">2015–2019: The slow awakening.</strong> After the post-2008 crash left Romanian real estate in a prolonged hangover, prices in Brașov started recovering quietly around 2015. Growth was modest — 2% to 7% per year — but consistent. By 2019, the average price had climbed from roughly €800/sqm to around €1,083/sqm. Not spectacular, but steady. The kind of growth that doesn't make headlines but makes early buyers quietly pleased with themselves.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                <strong className="text-stone-800">2019–2022: The pandemic paradox.</strong> Here's where things get counterintuitive. A global pandemic hit in 2020. Economies froze. And Brașov's real estate market... accelerated. The reason is straightforward in hindsight. Remote work changed what people wanted from housing. Suddenly, living in a city with mountains on the doorstep, clean air, and a lower cost of living than Bucharest or Cluj-Napoca made a lot of sense. Demand spiked. Supply couldn't keep up. Between September 2019 and September 2022, Brașov recorded the highest apartment price increase of any major Romanian city — up 52%, from €1,083 to €1,650 per square metre.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-8" style={{ lineHeight: "1.8" }}>
                <strong className="text-stone-800">2022–2025: Inflation, interest rates, and continued growth.</strong> The next phase was harder. Inflation peaked at 17% in late 2022. Mortgage rates climbed above 6%. In most Western European markets, this combination would have cooled prices significantly. In Brașov, prices kept rising — up another 34% between 2022 and 2025, reaching €2,203/sqm by September 2025. By March 2026, the latest data from Imobiliare.ro puts the average at €2,272/sqm for existing apartments, and €2,723/sqm for new builds.
              </p>

              {/* TABLE 1 */}
              <h2 className="text-stone-900 mt-10 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                The full picture: Brașov vs. Romania's major cities
              </h2>
              <div className="overflow-x-auto mb-4">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr style={{ backgroundColor: "oklch(0.14 0.01 60)", color: "oklch(0.95 0.006 75)" }}>
                      <th className="text-left px-4 py-3 font-semibold" style={{ fontFamily: "Playfair Display, serif" }}>City</th>
                      <th className="text-right px-4 py-3 font-semibold">Sept 2019 (€/sqm)</th>
                      <th className="text-right px-4 py-3 font-semibold">Sept 2025 (€/sqm)</th>
                      <th className="text-right px-4 py-3 font-semibold">6-year increase</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { city: "Brașov", y2019: "1,083", y2025: "2,203", pct: "+103%", highlight: true },
                      { city: "Cluj-Napoca", y2019: "1,645", y2025: "3,175", pct: "+93%", highlight: false },
                      { city: "Sibiu", y2019: "950", y2025: "1,921", pct: "+102%", highlight: false },
                      { city: "Constanța", y2019: "1,578", y2025: "2,257", pct: "+43%", highlight: false },
                      { city: "Bucharest", y2019: "1,187", y2025: "2,017", pct: "+70%", highlight: false },
                      { city: "Iași", y2019: "1,050", y2025: "1,815", pct: "+73%", highlight: false },
                      { city: "Timișoara", y2019: "1,050", y2025: "1,607", pct: "+53%", highlight: false },
                    ].map((row, i) => (
                      <tr key={i} style={{ backgroundColor: row.highlight ? "oklch(0.95 0.02 20)" : i % 2 === 0 ? "oklch(0.99 0.003 75)" : "white" }}>
                        <td className="px-4 py-3 font-semibold text-stone-800">{row.city}</td>
                        <td className="px-4 py-3 text-right text-stone-600">€{row.y2019}</td>
                        <td className="px-4 py-3 text-right text-stone-600">€{row.y2025}</td>
                        <td className="px-4 py-3 text-right font-bold" style={{ color: "oklch(0.38 0.12 15)" }}>{row.pct}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-stone-400 mb-8 italic">Source: Storia / OLX Imobiliare Romania, October 2025</p>

              <p className="text-stone-600 font-light leading-relaxed mb-8" style={{ lineHeight: "1.8" }}>
                The table tells an interesting story. Brașov and Sibiu are the only cities that more than doubled in six years. Cluj-Napoca, despite being Romania's most expensive market, grew slightly less in percentage terms. Bucharest — the capital, the economic engine — grew by "only" 70%. What this means for an investor: Brașov has been outperforming the national average consistently, and it's doing so from a price base that is still significantly lower than comparable cities in Central Europe.
              </p>

              <figure className="my-10">
                <img src={STREET_IMG} alt="Luxury apartment interior with mountain view, Brașov" className="w-full h-72 object-cover" loading="lazy" />
                <figcaption className="text-xs text-stone-400 mt-2 text-center">New developments in Brașov increasingly offer mountain views that were once reserved for hotels</figcaption>
              </figure>

              <InArticleBanner />

              <h2 className="text-stone-900 mt-10 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                Why Brașov specifically? The structural reasons
              </h2>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                Price growth in real estate is never random. Behind Brașov's numbers are a few structural factors that aren't going away.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                <strong className="text-stone-800">The airport.</strong> Brașov's new international airport opened in 2024 — a project that was discussed for decades and finally happened. Direct connections to Western Europe change the calculus for both tourists and investors. Cities with airports attract more international capital. This is not a coincidence.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                <strong className="text-stone-800">The infrastructure investment.</strong> The A3 motorway extension connecting Brașov to Bucharest is progressing. When completed, it will reduce the drive to the capital to under two hours. Improved connectivity historically pushes real estate prices up in the connected city.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                <strong className="text-stone-800">The tourism economy.</strong> Brașov receives millions of visitors per year. Short-term rental yields in the city centre and near ski resorts are among the highest in Romania. This creates sustained demand for investment-grade apartments from buyers who have no intention of living there.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-8" style={{ lineHeight: "1.8" }}>
                <strong className="text-stone-800">The quality of life premium.</strong> This is harder to quantify but easy to observe. Brașov consistently ranks at the top of Romanian quality-of-life surveys. Clean air, mountains, a functional city centre, lower crime than Bucharest — these factors attract a specific type of buyer who is willing to pay above the national average.
              </p>

              {/* TABLE 2 */}
              <h2 className="text-stone-900 mt-10 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                What the numbers look like today, by apartment type
              </h2>
              <div className="overflow-x-auto mb-4">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr style={{ backgroundColor: "oklch(0.14 0.01 60)", color: "oklch(0.95 0.006 75)" }}>
                      <th className="text-left px-4 py-3 font-semibold" style={{ fontFamily: "Playfair Display, serif" }}>Apartment type</th>
                      <th className="text-right px-4 py-3 font-semibold">Average price (existing)</th>
                      <th className="text-right px-4 py-3 font-semibold">Year-on-year change</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { type: "Studio / 1-room", price: "€2,266/sqm", change: "+5.2%" },
                      { type: "2-room", price: "€2,244/sqm", change: "+7.7%" },
                      { type: "3-room", price: "€2,153/sqm", change: "+9.3%" },
                      { type: "New builds (all types)", price: "€2,723/sqm", change: "+9.2%" },
                    ].map((row, i) => (
                      <tr key={i} style={{ backgroundColor: i % 2 === 0 ? "oklch(0.99 0.003 75)" : "white" }}>
                        <td className="px-4 py-3 font-semibold text-stone-800">{row.type}</td>
                        <td className="px-4 py-3 text-right text-stone-600">{row.price}</td>
                        <td className="px-4 py-3 text-right font-bold" style={{ color: "oklch(0.38 0.12 15)" }}>{row.change}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-stone-400 mb-8 italic">Source: Imobiliare.ro, March 2026</p>

              <p className="text-stone-600 font-light leading-relaxed mb-8" style={{ lineHeight: "1.8" }}>
                A few things stand out. First, new builds are commanding a 20–25% premium over existing stock — which reflects both the quality difference and the energy efficiency requirements of newer construction. Second, three-room apartments are growing fastest in percentage terms, which suggests that families and larger households are driving demand at the upper end.
              </p>

              <h2 className="text-stone-900 mt-10 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                Have you missed the train?
              </h2>
              <p className="text-stone-600 font-light leading-relaxed mb-6" style={{ lineHeight: "1.8" }}>
                This is the question everyone asks, and the honest answer is: probably not. Compared to equivalent cities in Central Europe, Brașov remains significantly undervalued. A comparable apartment in Budapest costs roughly double. In Prague, triple. In Vienna, four times as much. For a full picture of what it actually costs to live here day-to-day, see our <Link href="/cost-of-living-brasov" className="underline decoration-dotted underline-offset-2 hover:text-stone-900 transition-colors" style={{ color: "oklch(0.38 0.12 15)" }}>2026 cost of living breakdown</Link>. The gap is narrowing — that's what the growth numbers show — but it hasn't closed.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-6" style={{ lineHeight: "1.8" }}>
                The factors driving Brașov's growth are structural, not speculative. The airport is real. The motorway is being built. The quality of life is measurable. These don't reverse overnight. What has changed is the entry point. Buying in Brașov in 2015 at €800/sqm was a different risk profile than buying at €2,272/sqm in 2026. The upside is smaller, the capital required is larger, and the margin for error is thinner. Anyone who tells you otherwise is either selling you something or hasn't done the math.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-8" style={{ lineHeight: "1.8" }}>
                But "the train has left" is also not the right framing. The train has simply become a more expensive ticket.
              </p>

              {/* CTA */}
              <div className="mt-12 p-6 border border-stone-200" style={{ backgroundColor: "oklch(0.98 0.006 75)" }}>
                <p className="text-stone-600 font-light leading-relaxed text-sm mb-4">
                  <strong className="text-stone-900">Brașov Gateway tracks the local real estate market</strong> and helps foreign buyers navigate the purchase process in Romania — from identifying the right property to completing the legal paperwork. If you are a non-Romanian citizen, our <Link href="/buying-apartment-foreigner-brasov" className="text-stone-900 border-b border-dotted border-stone-400 hover:border-stone-900 transition-colors">step-by-step guide for foreign buyers</Link> covers the NIF, the Nordis Law, and all closing costs.
                </p>
                <Link href="/services/real-estate" className="inline-flex items-center gap-2 text-xs font-semibold no-underline" style={{ color: "oklch(0.38 0.12 15)", letterSpacing: "0.08em" }}>
                  SEE HOW I CAN HELP <ChevronRight size={12} />
                </Link>
              </div>

              <FeaturedDeveloper />
            </div>

            {/* RELATED ARTICLES */}
            <div className="mt-16 pt-10 border-t border-stone-200">
              <h3 className="text-stone-900 mb-8 text-xl font-bold" style={{ fontFamily: "Playfair Display, serif" }}>
                Continue reading
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {RELATED.map((r, i) => (
                  <Link key={i} href={r.href} className="group no-underline">
                    <div className="overflow-hidden mb-3">
                      <img src={r.img} alt={r.title} className="w-full h-36 object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                    </div>
                    <span className="text-xs uppercase tracking-widest font-semibold" style={{ color: "oklch(0.38 0.12 15)" }}>{r.tag}</span>
                    <h4 className="mt-2 text-stone-800 text-sm font-semibold leading-snug group-hover:text-stone-500 transition-colors" style={{ fontFamily: "Playfair Display, serif" }}>
                      {r.title}
                    </h4>
                  </Link>
                ))}
              </div>
            </div>
          </main>

          {/* SIDEBAR */}
          <aside className="lg:w-72 flex-shrink-0">
            <div className="sticky top-20 flex flex-col gap-6">
              <SidebarAd />
              <div className="border border-stone-200 p-5" style={{ backgroundColor: "oklch(0.985 0.006 75)" }}>
                <h4 className="font-bold text-stone-900 mb-2 text-sm" style={{ fontFamily: "Playfair Display, serif" }}>
                  About Brașov Gateway
                </h4>
                <p className="text-stone-500 text-xs font-light leading-relaxed">
                  We help foreigners move to, invest in, and build a life in Brașov. Real estate, company setup, relocation support — all in one place.
                </p>
                <Link href="/#contact" className="mt-4 flex items-center gap-1 text-xs font-semibold no-underline" style={{ color: "oklch(0.38 0.12 15)", letterSpacing: "0.08em" }}>
                  GET IN TOUCH <ChevronRight size={11} />
                </Link>
              </div>
              <div className="border border-stone-200 p-5">
                <h4 className="font-bold text-stone-900 mb-2 text-sm" style={{ fontFamily: "Playfair Display, serif" }}>Stay in the loop</h4>
                <p className="text-stone-500 text-xs font-light leading-relaxed mb-3">No spam. One email per week with what is actually happening in Brașov.</p>
                <NewsletterInlineLight />
              </div>
              {/* CTA card — Relocation support */}
              <div className="p-5 text-white" style={{ backgroundColor: "oklch(0.38 0.12 15)" }}>
                <p className="text-xs uppercase tracking-widest mb-3 opacity-70">Relocation support</p>
                <h4 className="font-bold mb-3 leading-snug" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.1rem" }}>
                  Moving to Brașov?
                </h4>
                <p className="text-sm font-light leading-relaxed mb-4 opacity-80">
                  We help expats find apartments, navigate bureaucracy, and settle in — without the stress.
                </p>
                <Link href="/contact">
                  <button className="w-full text-xs py-2.5 border border-white/40 hover:bg-white/10 transition-colors tracking-widest">
                    BOOK A FREE CALL
                  </button>
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <SiteFooter />
    </div>
    </>
  );
}
